import {FC, useState} from 'react';

import {Clear, Upload} from '@mui/icons-material';
import {Avatar, Box, IconButton} from '@mui/material';

import 'react-image-crop/dist/ReactCrop.css';
import {ProfilePhotoDeleteModal} from './ProfilePhotoDeleteModal';
import FileImageUploader from '../../../../../components/FileUploader/FileImageUploader';
import {greenColors, orangeColors} from '../../../../../layouts/theme/designSystem';

const Overlay: FC<{
  isMobile?: boolean;
}> = ({isMobile}) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        opacity: 0,
        transition: 'opacity 0.3s ease',
        borderRadius: '50%',
        boxSizing: 'border-box',
        '&:hover': {
          opacity: 1,
          border: `1px solid ${greenColors[40]}`,
          cursor: 'pointer',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: isMobile ? '84px' : '160px',
          height: isMobile ? '84px' : '160px',
        }}
      >
        <Box sx={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}>
          <Upload
            sx={{
              color: 'white',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const ProfilePhotoTrigger: FC<{
  isMobile?: boolean;
  imageSrc?: string | null;
}> = ({isMobile, imageSrc}) => {
  const colors = {
    border: orangeColors[100],
    hover: orangeColors[40],
    iconColor: orangeColors[900],
  };
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        flex: isMobile ? '84px' : '0 0 160px',
        height: isMobile ? '84px' : '160px',
        position: 'relative',
        borderRadius: '50%',
        boxSizing: 'border-box',
        border: imageSrc ? 'none' : `1px solid ${colors.border}`,
        '&:hover': {
          cursor: 'pointer',
          border: imageSrc ? 'none' : `1px solid ${colors.hover}`,
        },
      }}
    >
      <Box
        sx={{
          width: isMobile ? '84px' : '160px',
          height: isMobile ? '84px' : '160px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '50%',
          overflow: 'hidden',
        }}
      >
        {imageSrc ? (
          <>
            <Avatar
              src={imageSrc}
              sx={{
                width: '100%',
                height: '100%',
              }}
            />

            <Overlay isMobile={isMobile} />
          </>
        ) : (
          <Upload
            fontSize='large'
            sx={{
              color: colors.iconColor,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export interface Props {
  isMobile?: boolean;
  imageSrc?: string | null;
  onImageChange?: (image: File | null) => void;
}

export const ProfilePhoto: FC<Props> = ({isMobile, imageSrc, onImageChange}) => {
  const [open, setOpen] = useState(false);
  const [isHovered, setHovered] = useState(false);
  const colors = {
    background: orangeColors[40],
    border: orangeColors[100],
    hover: orangeColors[40],
    iconHover: orangeColors[900],
    iconColor: orangeColors[900],
  };

  if (!onImageChange) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'center',
          flex: isMobile ? '84px' : '0 0 160px',
          height: isMobile ? '84px' : '160px',
          position: 'relative',
          borderRadius: '50%',
          boxSizing: 'border-box',
          border: imageSrc ? 'none' : `1px solid ${colors.border}`,
          '&:hover': {
            border: imageSrc ? 'none' : `1px solid ${colors.hover}`,
          },
        }}
      >
        <Box
          sx={{
            width: isMobile ? '84px' : '160px',
            height: isMobile ? '84px' : '160px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '50%',
            overflow: 'hidden',
          }}
        >
          {imageSrc ? (
            <>
              <Avatar src={imageSrc} sx={{width: '100%', height: '100%'}} />
            </>
          ) : (
            <Avatar
              sx={{
                width: isMobile ? '84px' : '160px',
                height: isMobile ? '84px' : '160px',
              }}
            />
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{position: 'relative'}} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <ProfilePhotoDeleteModal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={() => {
          setOpen(false);
          onImageChange(null);
        }}
      />
      <FileImageUploader
        renderTrigger={() => <ProfilePhotoTrigger isMobile={isMobile} imageSrc={imageSrc} />}
        onImageChange={onImageChange}
      />
      {imageSrc && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setHovered(false);
            setOpen(true);
          }}
          sx={{
            display: isHovered ? 'flex' : 'none',
            position: 'absolute',
            top: 0,
            right: 0,
            width: isMobile ? '28px' : '40px',
            height: isMobile ? '28px' : '40px',
            background: colors.iconHover,
            borderRadius: '50%',
            justifyContent: 'center',
            alignItems: 'center',
            '&:hover': {
              cursor: 'pointer',
              background: colors.hover,
              color: colors.iconColor,
            },
            color: 'white',
          }}
        >
          <Clear />
        </IconButton>
      )}
    </Box>
  );
};

export default ProfilePhoto;
